import { Alert, Box, Text } from '@ksyos/design-system';
import type { ICostInfoAndConsentState } from '@common/validation/costInfoAndConsent';
import type { ValidationErrors } from '@frontend/utils/validation';
import { translateKey } from '@frontend/translate';
import { calculatePriceWithVat } from '@frontend/utils/calculatePriceWithVat';
import { FormatCurrency } from '../../FormatCurrency';

interface ICostAndPaymentProps {
    onchange: (value: boolean, name: string) => void;
    price: number;
    serviceName: string;
    showNoConsentAlert: boolean | undefined;
    state: ICostInfoAndConsentState;
    validationErrors: ValidationErrors;
    vatRate: number;
}

enum Insurance {
    foreigner = 'foreigner',
    prisoner = 'prisoner',
    uninsured = 'uninsured',
}

export function CostAndPayment(props: ICostAndPaymentProps) {
    const insuranceType = Insurance.uninsured;

    return (
        <>
            <Alert appearance="info">{translateKey('costInfoAndConsentModal.costAndPaymentStep.alert')}</Alert>
            <Box
                display="flex"
                flexDirection={['column', null, 'row']}
                justifyContent="space-around"
                marginTop={6}
            >
                <Box
                    alignContent="center"
                    display="flex"
                    flexDirection="column"
                    marginBottom={5}
                    width={['100%', null, '23%']}
                >
                    <Box style={{ margin: '0 auto' }}>
                        <img
                            alt="invoice"
                            src="/assets/images/invoice.svg"
                            style={{ width: '100%' }}
                        />
                    </Box>
                    <Text
                        marginTop={4}
                        textAlign="center"
                    >
                        {translateKey('costInfoAndConsentModal.costAndPaymentStep.information1a')}
                        <b>{props.serviceName}</b>
                        {translateKey('costInfoAndConsentModal.costAndPaymentStep.information1b')}
                        <b>
                            <FormatCurrency value={calculatePriceWithVat(props.price, props.vatRate)} />
                        </b>
                        <span style={{ fontSize: '10px' }}>
                            {translateKey(
                                'costInfoAndConsentModal.costAndPaymentStep.information1c',
                                `${props.vatRate}`
                            )}
                        </span>
                    </Text>
                </Box>
                <Box
                    alignContent="center"
                    display="flex"
                    flexDirection="column"
                    marginBottom={5}
                    width={['100%', null, '23%']}
                >
                    <Box style={{ margin: '0 auto' }}>
                        <img
                            alt="post-or-email"
                            src="/assets/images/post-or-email.svg"
                            style={{ width: '100%' }}
                        />
                    </Box>
                    <Text
                        marginTop={4}
                        textAlign="center"
                    >
                        {getText(2, insuranceType)}
                    </Text>
                </Box>
                <Box
                    alignContent="center"
                    display="flex"
                    flexDirection="column"
                    marginBottom={5}
                    width={['100%', null, '23%']}
                >
                    <Box style={{ margin: '0 auto' }}>
                        <img
                            alt="patient-pays"
                            src="/assets/images/patient-pays.svg"
                            style={{ width: '100%' }}
                        />
                    </Box>
                    <Text
                        marginTop={4}
                        textAlign="center"
                    >
                        {getText(3, insuranceType)}
                    </Text>
                </Box>
            </Box>
            <Alert appearance={!props.showNoConsentAlert || props.state.hasCostConsentPatient ? 'info' : 'warning'}>
                <Box sx={{ mb: 3 }}>
                    {!props.showNoConsentAlert || props.state.hasCostConsentPatient
                        ? translateKey('costInfoAndConsentModal.costAndPaymentStep.patientAgreement')
                        : translateKey('costInfoAndConsentModal.costAndPaymentStep.noPatientAgreement')}
                </Box>
                <Alert.Checkbox
                    checked={props.state.hasCostConsentPatient || false}
                    label={
                        <Alert.Text
                            as="span"
                            size="sm"
                        >
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.checkbox')}
                        </Alert.Text>
                    }
                    onChange={(e) => props.onchange(e.target.checked, 'hasCostConsentPatient')}
                />
            </Alert>
        </>
    );

    function getText(column: 2 | 3, insurance: Insurance) {
        if (column === 2) {
            if (insurance === Insurance.prisoner) {
                return (
                    <>
                        {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner2a')}{' '}
                        <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner2b')}</b>{' '}
                        {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner2c')}{' '}
                        <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner2d')}</b>
                    </>
                );
            }

            return (
                <>
                    {translateKey('costInfoAndConsentModal.costAndPaymentStep.information2a')}
                    <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.information2b')}</b>
                </>
            );
        }

        if (column === 3) {
            switch (insurance) {
                default:
                case Insurance.uninsured:
                    return (
                        <>
                            <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationUninsured3a')}</b>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationUninsured3b')}
                            <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationUninsured3c')}</b>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationUninsured3d')}
                        </>
                    );
                case Insurance.foreigner:
                    return (
                        <>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationForeigner3a')}
                            <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationForeigner3b')}</b>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationForeigner3c')}
                            <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationForeigner3d')}</b>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationForeigner3e')}
                        </>
                    );
                case Insurance.prisoner:
                    return (
                        <>
                            {translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner3a')}
                            <b>{translateKey('costInfoAndConsentModal.costAndPaymentStep.informationPrisoner3b')}</b>
                        </>
                    );
            }
        }

        return null;
    }
}
